import type { ManualDoc } from "../types.ts"
import { t } from "./t.ts"

export const START_DOCS: ManualDoc[] = [
  {
    slug: "intro",
    category: "start",
    order: 1,
    title: t("시스템 소개", "システムの紹介"),
    summary: t(
      "화주와 쿨트랙JP가 같은 화면에서 반품 한 건을 끝까지 처리합니다.",
      "荷主と日本側が同じ画面で返品を最後まで処理します。",
    ),
    keywords: ["개요", "흐름", "프로세스", "전체", "概要", "流れ"],
    body: [
      {
        kind: "p",
        text: t(
          "한국에서 일본 고객에게 보낸 상품이 되돌아올 때, 그 한 건을 두 회사가 나눠 처리하는 시스템입니다. 예전에 구글시트 한 장을 함께 쓰며 손으로 적던 일을 대신합니다.",
          "韓国から日本のお客様へ送った商品が戻ってくるとき、その1件を2社で分担して処理するシステムです。以前はGoogleスプレッドシート1枚を共有して手で書いていた作業に代わるものです。",
        ),
      },
      { kind: "heading", text: t("일이 흘러가는 순서", "作業の流れ") },
      {
        kind: "table",
        head: [t("단계", "段階"), t("하는 일", "内容"), t("하는 쪽", "担当")],
        rows: [
          [t("1", "1"), t("반품 접수 등록(상품·수량·사유·고객)", "返品の受付登録(商品・数量・理由・顧客)"), t("화주", "荷主")],
          [t("2", "2"), t("상자 수령 — 입고 스캔, 중량·착불비", "箱の受領 — 入庫スキャン、重量・着払い費用"), t("쿨트랙JP", "日本側")],
          [t("3", "3"), t("검수 — 줄마다 실제 수량을 센다", "検品 — 行ごとに実数量を数える"), t("쿨트랙JP", "日本側")],
          [t("4", "4"), t("입고 내용 확인, 처리방안(한국반송/폐기)", "入庫内容の確認、処理方針(韓国返送/廃棄)"), t("화주", "荷主")],
          [t("5", "5"), t("인보이스 품목 기재(품명·HS코드·원산지·단가)", "インボイス品目の記入(品名・HSコード・原産地・単価)"), t("쿨트랙JP", "日本側")],
          [t("6", "6"), t("서류 확정", "書類の確定"), t("화주", "荷主")],
          [t("7", "7"), t("선적 → 출고(세이노) → 오사카 도착", "船積み → 出庫(西濃) → 大阪着"), t("쿨트랙JP", "日本側")],
          [t("8", "8"), t("일본 출항(팬스타 추적번호)", "日本出航(パンスター追跡番号)"), t("쿨트랙JP", "日本側")],
          [t("9", "9"), t("한국 입고 → 반송완료", "韓国入庫 → 返送完了"), t("화주", "荷主")],
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "적는 쪽과 확인하는 쪽이 갈려 있습니다. 실물을 들고 있는 쿨트랙JP가 세고 적고, 주문을 아는 화주가 확인합니다. 한쪽이 적고 스스로 확정하면 확인이 아니기 때문입니다.",
          "記入する側と確認する側を分けています。現物を持つ日本側が数えて記入し、注文を知る荷主が確認します。同じ人が記入して確定すれば確認になりません。",
        ),
      },
      { kind: "heading", text: t("반품 한 건이 지나는 상태", "返品1件がたどる状態") },
      {
        kind: "p",
        text: t(
          "접수완료 → 입고완료 → 서류완료 → 출고 → 반송완료 순서로 갑니다. 폐기와 접수취소는 중간에 끝나는 길입니다.",
          "受付完了 → 入庫完了 → 書類完了 → 出庫 → 返送完了 の順に進みます。廃棄と受付取消は途中で終わる道です。",
        ),
      },
      {
        kind: "list",
        items: [
          t(
            "두 회사가 같은 건을 보지만 적을 수 있는 칸이 다릅니다.",
            "2社が同じ件を見ますが、記入できる欄は異なります。",
          ),
          t(
            "누구 일인지는 색과 이름표로 구분합니다 — 쿨트랙JP는 청록(상자 표시), 화주는 보라(서류 표시). 반품 상세의 지금 할 일·요약·이력에서 같은 색으로 나옵니다.",
            "どちらの作業かは色とラベルで見分けます — 日本側は青緑(箱のマーク)、荷主は紫(書類のマーク)。返品詳細の「今やること」・概要・履歴で同じ色になります。",
          ),
          t(
            "모든 변경은 누가 언제 했는지 이력에 남습니다(반품 상세 → 이력).",
            "すべての変更は誰がいつ行ったか履歴に残ります(返品詳細 → 履歴)。",
          ),
          t(
            "화면 언어는 계정마다 한국어·일본어 중에서 고릅니다.",
            "画面の言語はアカウントごとに韓国語・日本語から選べます。",
          ),
        ],
      },
    ],
  },

  {
    slug: "account",
    category: "start",
    order: 2,
    title: t("로그인과 내 계정", "ログインとアカウント"),
    summary: t(
      "로그인, 화면 언어 바꾸기, 비밀번호 변경.",
      "ログイン、画面言語の切り替え、パスワードの変更。",
    ),
    keywords: ["로그인", "비밀번호", "패스워드", "언어", "로그아웃", "ログイン", "パスワード", "言語"],
    body: [
      { kind: "heading", text: t("로그인", "ログイン") },
      {
        kind: "steps",
        items: [
          t("브라우저에서 return.cooltrack.jp 로 들어갑니다.", "ブラウザで return.cooltrack.jp を開きます。"),
          t("아이디와 비밀번호를 칩니다.", "IDとパスワードを入力します。"),
          t("로그인 단추를 누릅니다.", "ログインボタンを押します。"),
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "아이디는 이메일 주소가 아닙니다. 사내에서 쓰는 짧은 아이디입니다(예: ics, cooltrack).",
          "IDはメールアドレスではありません。社内で使う短いIDです(例: ics, cooltrack)。",
        ),
      },
      { kind: "heading", text: t("내 계정", "アカウント") },
      {
        kind: "list",
        items: [
          t("내 이름·아이디·역할을 확인합니다.", "自分の名前・ID・役割を確認します。"),
          t(
            "화면 언어를 한국어와 日本語 중에서 고릅니다. 고르면 바로 바뀝니다.",
            "画面の言語を韓国語と日本語から選びます。選ぶとすぐ切り替わります。",
          ),
        ],
      },
      { kind: "heading", text: t("비밀번호 변경", "パスワードの変更") },
      {
        kind: "steps",
        items: [
          t("왼쪽 메뉴에서 내 계정을 엽니다.", "左メニューからアカウントを開きます。"),
          t("비밀번호 카드의 비밀번호 변경을 누릅니다.", "パスワードのカードにある「パスワードの変更」を押します。"),
          t(
            "현재 비밀번호, 새 비밀번호, 새 비밀번호 확인을 채우고 비밀번호 변경을 누릅니다.",
            "現在のパスワード、新しいパスワード、新しいパスワード(確認)を入力し、「パスワードの変更」を押します。",
          ),
        ],
      },
      {
        kind: "list",
        items: [
          t("새 비밀번호는 10자 이상이어야 합니다.", "新しいパスワードは10文字以上です。"),
          t("지금 쓰는 비밀번호와 같으면 받지 않습니다.", "現在と同じパスワードは使えません。"),
          t(
            "바꾸면 그 계정의 모든 기기에서 로그아웃됩니다. 새 비밀번호로 다시 로그인하세요.",
            "変更すると、そのアカウントの全端末からログアウトされます。新しいパスワードで再度ログインしてください。",
          ),
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "비밀번호를 잊으면 화면에서는 되돌릴 수 없습니다. 관리자에게 재발급을 요청하세요.",
          "パスワードを忘れた場合、画面からは戻せません。管理者に再発行を依頼してください。",
        ),
      },
    ],
  },

  {
    slug: "screen",
    category: "start",
    order: 3,
    title: t("화면 둘러보기", "画面の見かた"),
    summary: t(
      "왼쪽 메뉴, 위쪽 줄의 검색·알림·다크 모드, 할 일 화면.",
      "左メニュー、上部の検索・通知・ダークモード、対応待ち画面。",
    ),
    keywords: ["메뉴", "대시보드", "할 일", "검색", "알림", "다크", "모바일", "メニュー", "通知", "ダークモード"],
    body: [
      { kind: "heading", text: t("왼쪽 메뉴", "左メニュー") },
      {
        kind: "table",
        head: [t("메뉴", "メニュー"), t("하는 일", "内容"), t("보이는 역할", "表示される役割")],
        rows: [
          [t("할 일", "対応待ち"), t("지금 손이 필요한 건을 숫자로 모아 보여 줍니다.", "今対応が必要な件を数字でまとめて表示します。"), t("모두", "全員")],
          [t("반품 목록", "返品一覧"), t("모든 반품 건을 찾고 거릅니다.", "すべての返品を検索・絞り込みします。"), t("모두", "全員")],
          [t("반품 접수", "返品受付"), t("새 반품 건을 등록합니다.", "新しい返品を登録します。"), t("화주·관리자", "荷主・管理者")],
          [t("입고 스캔", "入庫スキャン"), t("상자 바코드를 찍어 입고 처리합니다.", "箱のバーコードを読み取って入庫します。"), t("쿨트랙JP·관리자", "日本側・管理者")],
          [t("선적", "船積み"), t("출고 대기 건을 묶어 보내고 인보이스를 내려받습니다.", "出庫待ちの件をまとめて送り、インボイスをダウンロードします。"), t("모두", "全員")],
          [t("품목 마스터", "品目マスタ"), t("인보이스에 쓰는 품명·HS코드·원산지 목록입니다.", "インボイスで使う品名・HSコード・原産地の一覧です。"), t("모두(수정은 관리자)", "全員(編集は管理者)")],
          [t("내 계정", "アカウント"), t("언어와 비밀번호를 바꿉니다.", "言語とパスワードを変更します。"), t("모두", "全員")],
          [t("매뉴얼", "マニュアル"), t("지금 보고 있는 이 문서입니다.", "今ご覧のこの文書です。"), t("모두", "全員")],
        ],
      },
      { kind: "heading", text: t("위쪽 줄", "上部のバー") },
      {
        kind: "list",
        items: [
          t(
            "통합 검색 — 주문번호·반송장번호·고객명으로 반품 건을 바로 찾습니다. 어느 화면에서든 Ctrl+K(맥은 ⌘K)로 열립니다.",
            "統合検索 — 注文番号・返送伝票番号・顧客名で返品をすぐ探せます。どの画面でも Ctrl+K(Macは⌘K)で開きます。",
          ),
          t(
            "알림(종 모양) — 놓치면 곤란한 일을 모아 알립니다. 누르면 그 조건으로 걸러진 목록으로 갑니다.",
            "通知(ベル) — 見落とすと困ることをまとめて知らせます。押すとその条件で絞り込んだ一覧に移動します。",
          ),
          t(
            "어둡게 보기 — 화면을 어둡게 바꿉니다. 고른 값은 그 브라우저에만 남습니다.",
            "ダークモード — 画面を暗くします。選んだ設定はそのブラウザにのみ残ります。",
          ),
          t("계정 — 언어를 바꾸거나 로그아웃합니다.", "アカウント — 言語の変更やログアウトを行います。"),
        ],
      },
      { kind: "heading", text: t("할 일 화면", "対応待ち画面") },
      {
        kind: "p",
        text: t(
          "로그인하면 처음 보이는 화면입니다. 역할에 따라 다른 카드가 보이고, 카드의 숫자를 누르면 그 조건으로 걸러진 반품 목록으로 바로 갑니다.",
          "ログイン後に最初に表示される画面です。役割によって表示されるカードが異なり、カードの数字を押すとその条件で絞り込んだ返品一覧に移動します。",
        ),
      },      {
        kind: "image",
        src: t("/help/dashboard-ko.png", "/help/dashboard-ja.png"),
        alt: t("할 일 화면의 카드들", "対応待ち画面のカード"),
        caption: t(
          "화주 계정으로 본 할 일 화면. 급한 일이 위에, 내 차례가 아래에 놓입니다.",
          "荷主のアカウントで見た対応待ち画面。至急のものが上、自分の番が下に並びます。",
        ),
      },

      {
        kind: "note",
        tone: "info",
        text: t(
          "휴대폰에서는 왼쪽 메뉴가 숨겨지고 왼쪽 위 메뉴 단추로 엽니다. 표는 한 건씩 카드로 바뀌어 보입니다.",
          "スマートフォンでは左メニューが隠れ、左上のメニューボタンで開きます。表は1件ずつカード表示に変わります。",
        ),
      },
    ],
  },
]
